import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PartnerTypeLabel } from 'vopay';

/**
 * Raw API value stays the logic key (`getProgramTermsVariant` switches on it); only the rendered
 * string is localized (i18n-conventions rule). Falls back to the raw label when a type isn't mapped,
 * so one the backend adds before the union catches up still renders readably, just untranslated.
 * The `Record` is exhaustive over the union, so tsc fails if a member is dropped.
 */
export default function usePartnerTypeLabels() {
  const { t } = useTranslation('translations', { keyPrefix: 'Global.PartnerTypeLabels' });

  return useMemo(() => {
    const labelsByType: Record<Exclude<PartnerTypeLabel, ''>, string> = {
      'Referral ISV': t('Referral ISV', { defaultValue: 'Referral ISV' }),
      'Referral ISO': t('Referral ISO', { defaultValue: 'Referral ISO' }),
      'Referral - ISV RevShare': t('Referral - ISV RevShare', { defaultValue: 'Referral - ISV RevShare' }),
      'VAR - PaaS': t('VAR - PaaS', { defaultValue: 'VAR - PaaS' }),
      'VAR - PayFac (Non-Reporting)': t('VAR - PayFac (Non-Reporting)', {
        defaultValue: 'VAR - PayFac (Non-Reporting)',
      }),
      'VAR - PayFac (Reporting)': t('VAR - PayFac (Reporting)', { defaultValue: 'VAR - PayFac (Reporting)' }),
      'Technology Partner': t('Technology Partner', { defaultValue: 'Technology Partner' }),
      'Inside Sales': t('Inside Sales', { defaultValue: 'Inside Sales' }),
    };

    const labels: Record<string, string> = labelsByType;

    return {
      /** Falls back to the raw label if it isn't (yet) mapped. */
      resolvePartnerTypeLabel: (rawLabel: string): string => labels[rawLabel] ?? rawLabel,
    };
  }, [t]);
}
